import { useMemo, useEffect, useCallback } from 'react'
import type { DependencyList } from 'react'

import { z } from 'zod'

import { voidAsync } from '~publish/helpers/async/voidAsync'

const CanvaTransportMessage = z.object({
  type: z.literal('canva-return'),
  designId: z.string(),
  uid: z.string(),
  exportUrl: z.string(),
})

export type CanvaTransportMessage = z.infer<typeof CanvaTransportMessage>

/**
 * Subscribe to a broadcast channel that will receive messages
 * from another tab instance of buffer that will message after the canva design is complete
 * @param uid id of the desgin session
 * @param onMessage callback to handle the message
 */
export const useBroadcastChannel = (
  uid: string,
  onMessage: (message: CanvaTransportMessage) => void | Promise<void>,
  deps: DependencyList,
): void => {
  const broadcaster = useMemo(
    () => new BroadcastChannel(`canva-return-${uid}`),
    [uid],
  )

  // handler is memoized to prevent re-creation on every render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handler = useCallback(onMessage, deps)

  useEffect(() => {
    const listener = voidAsync(async (event: MessageEvent): Promise<void> => {
      const data = CanvaTransportMessage.safeParse(event.data)
      if (!data.success) return
      const { data: message } = data
      await handler(message)
      // BroadcastChannel is restricted to same-origin contexts and has no targetOrigin argument.
      // oxlint-disable-next-line unicorn/require-post-message-target-origin
      broadcaster.postMessage({ type: 'canva-return-success', uid })
    })
    broadcaster.addEventListener('message', listener)
    return (): void => {
      broadcaster.removeEventListener('message', listener)
    }
  }, [handler, broadcaster, uid])
}
